import styled from "@emotion/styled";
import type { FC } from "react";
import { useBloc as useBlocNext } from "@blac/react";
import { IconClose } from "src/constants/icons";
import { iconUrlClipboardCheck } from "src/constants/iconUrls";
import translate from "src/lib/translate";
import ObservationBundleBloc from "src/state/ObservationBundleBloc/ObservationBundleBloc";
import UserPreferencesCubit, {
  UserPreferenceKeys
} from "src/state/UserPreferencesCubit/UserPreferencesCubit";
import Translate from "src/ui/components/Translate/Translate";

//#region Styled components
const Card = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  border-radius: 0.5rem;
  overflow: hidden;
  background: var(--color-cream-darker);
  border: 1px solid rgba(170, 167, 160, 0.5);
`;

const cardTextBase = `
  font-family: "STK Bureau";
  font-size: 14px;
  font-style: normal;
  line-height: 140%;
  letter-spacing: -0.28px;
  font-feature-settings: 'liga' off, 'clig' off;
`;

const TextBlock = styled.div`
  padding-right: 1.25rem;
`;

const Title = styled.span`
  ${cardTextBase}
  font-weight: 500;
`;

const Description = styled.span`
  ${cardTextBase}
  font-weight: 400;
`;

const ImageContainer = styled.div`
  width: 4rem;
  height: 4rem;
  margin-right: -1rem;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const DismissButton = styled.button`
  position: absolute;
  top: 0.25rem;
  right: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;
//#endregion

const LabImportBanner: FC = () => {
  const [{ observationBundles }, { hasImportedBundle }] = useBlocNext(
    ObservationBundleBloc
  );
  const [{ loading }, { updateUserPreferences, isLabsImportBannerDismissed }] =
    useBlocNext(UserPreferencesCubit);

  const show =
    !loading &&
    !isLabsImportBannerDismissed &&
    hasImportedBundle(observationBundles ?? []);

  if (!show) {
    return null;
  }

  const dismiss = (): void => {
    void updateUserPreferences({
      [UserPreferenceKeys.labsImportBannerDismissed]: true
    });
  };

  return (
    <Card role="article">
      <TextBlock>
        <Title>
          <Translate msg="labResults.importBanner.title" />
        </Title>
        <Description> - </Description>
        <Description>
          <Translate msg="labResults.importBanner.text" />
        </Description>
      </TextBlock>
      <ImageContainer>
        <img src={iconUrlClipboardCheck} alt="" aria-hidden="true" />
      </ImageContainer>
      <DismissButton
        type="button"
        aria-label={translate("labResults.importBanner.dismiss")}
        onClick={dismiss}
      >
        <IconClose />
      </DismissButton>
    </Card>
  );
};

export default LabImportBanner;
